import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

const UploadContext = createContext();

export function UploadProvider({ children }) {
  const [uploads, setUploads] = useState({});
  const [allFileUploadsComplete, setAllFileUploadsComplete] = useState(false);
  const [requiredUploads, setRequiredUploads] = useState([]);
  const [optionalUploads, setOptionalUploads] = useState([]);

  const resetUploadContextState = useCallback(() => {
    setUploads({});
    setAllFileUploadsComplete(false);
    setRequiredUploads([]);
    setOptionalUploads([]);
    console.log('UploadContext state has been reset.');
  }, []);

  // Register a new file upload component
  const registerFileUpload = useCallback((id) => {
    setUploads((prev) => {
      // Only register if not already registered
      if (prev[id] === undefined) {
        return {
          ...prev,
          [id]: false, // false means not ready yet
        };
      }
      return prev;
    });
  }, []);

  // Set required uploads for the current template
  const setRequiredUploadsForTemplate = useCallback(
    (templateInputDocuments, templateOptionalDocuments) => {
      const optionalDocs = templateOptionalDocuments || [];
      setOptionalUploads(optionalDocs);

      // Filter out optional documents from required uploads
      const requiredDocs = (templateInputDocuments || []).filter(
        (doc) => !optionalDocs.includes(doc),
      );

      setRequiredUploads(requiredDocs);
      console.log('Required uploads:', requiredDocs);
      console.log('Optional uploads:', optionalDocs);
    },
    [],
  );

  // Mark a file upload as ready
  const markFileUploadReady = useCallback((id) => {
    setUploads((prev) => ({
      ...prev,
      [id]: true, // true means ready
    }));
  }, []);

  // Check if all required uploads are ready
  useEffect(() => {
    // If no required uploads, return
    if (requiredUploads.length === 0) {
      setAllFileUploadsComplete(false);
      return;
    }

    // Check if all required uploads are ready (excluding optional ones)
    const allReady = requiredUploads.every((id) => uploads[id] === true);

    setAllFileUploadsComplete(allReady);
  }, [uploads, requiredUploads, optionalUploads]);

  const value = {
    allFileUploadsComplete,
    registerFileUpload,
    markFileUploadReady,
    setRequiredUploadsForTemplate,
    resetUploadContextState,
    optionalUploads,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
}

export function useUploadContext() {
  return useContext(UploadContext);
}
